import { humanizeEventDate } from "@/lib/utflykt/humanizeEventDate";
import { Calendar, Wine, ShoppingBasket, Home, UtensilsCrossed, BookOpen, TreePine, Compass } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useProducerOfferings } from "@/hooks/useProducerOfferings";
import { useTranslate } from "@/i18n";

/** Map canonical offering keys to icon + i18n key */
const OFFERING_CHIP_MAP: Record<string, { Icon: LucideIcon; i18nKey: string }> = {
  accommodation:      { Icon: Home,            i18nKey: "producer.offering.accommodation" },
  food_fika:          { Icon: UtensilsCrossed, i18nKey: "producer.offering.food_fika" },
  farm_shop:          { Icon: ShoppingBasket,  i18nKey: "producer.offering.farm_shop" },
  preorder_pickup:    { Icon: ShoppingBasket,  i18nKey: "producer.offering.preorder_pickup" },
  other_farm_sales:   { Icon: Wine,            i18nKey: "producer.offering.other_farm_sales" },
  courses_workshops:  { Icon: BookOpen,        i18nKey: "producer.offering.courses_workshops" },
  guided_tours:       { Icon: Compass,         i18nKey: "producer.offering.guided_tours" },
  events:             { Icon: Calendar,        i18nKey: "producer.offering.events" },
  animals_nature:     { Icon: TreePine,        i18nKey: "producer.offering.animals_nature" },
};

interface ProducerSignalsRowProps {
  hasFarmSaleAlcohol?: boolean;
  hasFarmShop?: boolean;
  nextEventAt?: string | null;
  producerId?: string;
}

/**
 * Calm signal chips shown directly below hero on the producer public page.
 * Max 3 chips. Click scrolls to #kontakt.
 */
export function ProducerSignalsRow({
  hasFarmSaleAlcohol,
  hasFarmShop,
  nextEventAt,
  producerId,
}: ProducerSignalsRowProps) {
  const t = useTranslate();
  const { offerings: offeringKeys } = useProducerOfferings(producerId);
  const chips: { Icon: LucideIcon; label: string }[] = [];
  const seenKeys = new Set<string>();

  // 1. Time-sensitive event chip (highest priority)
  const eventLabel = humanizeEventDate(nextEventAt ?? null);
  if (eventLabel) {
    chips.push({ Icon: Calendar, label: eventLabel });
  }

  // 2. Offering-derived chips (deduplicate by key)
  for (const key of offeringKeys) {
    const chip = OFFERING_CHIP_MAP[key];
    if (!chip || seenKeys.has(key)) continue;
    chips.push({ Icon: chip.Icon, label: t(chip.i18nKey) });
    seenKeys.add(key);
  }

  // 3. Legacy flag fallbacks (temporary compatibility)
  if (hasFarmSaleAlcohol && !seenKeys.has("other_farm_sales")) {
    chips.push({ Icon: Wine, label: t("producer.offering.other_farm_sales") });
    seenKeys.add("other_farm_sales");
  }
  if (hasFarmShop && !seenKeys.has("farm_shop")) {
    chips.push({ Icon: ShoppingBasket, label: t("producer.offering.farm_shop") });
    seenKeys.add("farm_shop");
  }

  const visible = chips.slice(0, 3);
  const showFarmSaleNote = hasFarmSaleAlcohol || offeringKeys.includes('other_farm_sales');
  const showBoendeNote = offeringKeys.includes('accommodation');

  if (visible.length === 0) return null;

  const scrollToKontakt = () => {
    const el = document.getElementById("kontakt");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="space-y-2">
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
        {t("producer.signals.hereYouFind")}
      </p>
      <div className="flex flex-wrap gap-2">
        {visible.map((c) => (
          <button
            key={c.label}
            type="button"
            onClick={scrollToKontakt}
            aria-label={`${c.label} – visa detaljer`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted text-xs font-medium text-muted-foreground hover:bg-accent transition-colors cursor-pointer"
          >
            <c.Icon aria-hidden className="h-3.5 w-3.5 shrink-0" />
            {c.label}
          </button>
        ))}
      </div>
      {(showBoendeNote || showFarmSaleNote) && (
        <div className="space-y-0.5 pt-0.5">
          {showBoendeNote && (
            <p className="text-[11px] text-muted-foreground">
              {t("producer.signals.accommodationNote")}
            </p>
          )}
          {showFarmSaleNote && (
            <p className="text-[11px] text-muted-foreground">
              {t("producer.signals.farmSaleNote")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
